import api from '@/services/apiService';
import type { PadelGeneratePayload } from '@/components/community/padel/padelGenerateUtils';

export interface PadelAccess {
  canEdit: boolean;
  canRead: boolean;
}

export interface PadelSetScore {
  setNumber: number;
  scoreSideA: number;
  scoreSideB: number;
  isComplete: boolean;
  elapsedMs?: number;
}

export interface PadelGamePlayerItem {
  participantId: number;
  playerName: string;
  avatarUrl?: string;
  side: string;
  slotOrder: number;
}

export type PadelTimerStatus = 'idle' | 'running' | 'paused';
export type PadelTimerAction = 'start' | 'pause' | 'reset';

export interface PadelGameListItem {
  id: number;
  slug: string;
  sessionId: number;
  sessionSlug: string;
  sessionTitle: string;
  sessionType: string;
  scoringType: 'points' | 'sets' | 'time';
  targetScore?: number;
  roundNumber?: number;
  courtNumber?: number;
  maxSets?: number;
  status: string;
  winnerSide?: string;
  scoreSideA?: number;
  scoreSideB?: number;
  sideAPlayers: PadelGamePlayerItem[];
  sideBPlayers: PadelGamePlayerItem[];
  setScores?: PadelSetScore[];
  timerStatus?: PadelTimerStatus | string;
  timerSetNumber?: number;
  timerElapsedMs?: number;
  timerStartedAt?: string;
  serverTime?: string;
}

export interface PadelSessionListItem {
  id: number;
  slug: string;
  title: string;
  sessionType: string;
  rotationFormat?: string;
  scoringType: 'points' | 'sets' | 'time';
  targetScore?: number;
  numRounds: number;
  numCourts?: number;
  defaultMaxSets?: number | null;
  teams?: number[][];
  status: string;
  games?: PadelGameListItem[];
}

export interface PadelRoundDetail {
  roundNumber: number;
  status: string;
  queueParticipantIds: number[];
  game?: PadelGameListItem;
  games?: PadelGameListItem[];
}

export interface PadelSessionDetail extends PadelSessionListItem {
  rounds: PadelRoundDetail[];
}

export interface PadelStandingItem {
  position: number;
  participantId: number;
  playerName: string;
  avatarUrl?: string;
  gamesPlayed: number;
  pointsScored: number;
  pointsConceded: number;
  wins: number;
  losses: number;
  winRate: number;
}

export interface PadelGameDetail extends PadelGameListItem {
  eventSlug: string;
  canEdit: boolean;
}

export interface PadelTimerPayload {
  action: PadelTimerAction;
  setNumber: number;
}

const padelService = {
  async getAccess(eventSlug: string): Promise<PadelAccess> {
    const res = await api.get(`/mabar/events/${eventSlug}/padel/access`);
    return res.data.data;
  },

  async listSessions(eventSlug: string): Promise<PadelSessionListItem[]> {
    const res = await api.get(`/mabar/events/${eventSlug}/padel/sessions`);
    return res.data.data ?? [];
  },

  async getStandings(eventSlug: string): Promise<PadelStandingItem[]> {
    const res = await api.get(`/mabar/events/${eventSlug}/padel/standings`);
    return res.data.data ?? [];
  },

  async generateSession(
    eventSlug: string,
    payload: PadelGeneratePayload | {
      numRounds: number;
      scoringType: string;
      targetScore?: number;
    }
  ): Promise<PadelSessionListItem> {
    const res = await api.post(`/mabar/events/${eventSlug}/padel/sessions/generate`, payload);
    return res.data.data;
  },

  async createManualGame(
    eventSlug: string,
    payload: {
      scoringType: string;
      targetScore?: number;
      sideA: number[];
      sideB: number[];
    }
  ): Promise<PadelGameDetail> {
    const res = await api.post(`/mabar/events/${eventSlug}/padel/sessions/manual-game`, payload);
    return res.data.data;
  },

  async getGame(gameSlug: string): Promise<PadelGameDetail> {
    const res = await api.get(`/mabar/padel/games/${gameSlug}`);
    return res.data.data;
  },

  async getSessionDetail(sessionSlug: string): Promise<PadelSessionDetail> {
    const res = await api.get(`/mabar/padel/sessions/${sessionSlug}`);
    return res.data.data;
  },

  async saveScores(
    gameSlug: string,
    payload: {
      matchScore?: { scoreSideA: number; scoreSideB: number };
      sets?: { setNumber: number; scoreSideA: number; scoreSideB: number }[];
    }
  ): Promise<PadelGameDetail> {
    const res = await api.patch(`/mabar/padel/games/${gameSlug}/scores`, payload);
    return res.data.data;
  },

  async updateTimer(gameSlug: string, payload: PadelTimerPayload): Promise<PadelGameDetail> {
    const res = await api.patch(`/mabar/padel/games/${gameSlug}/timer`, payload);
    return res.data.data;
  },

  async deleteGame(gameSlug: string): Promise<void> {
    await api.delete(`/mabar/padel/games/${gameSlug}`);
  },

  async deleteSession(sessionSlug: string): Promise<void> {
    await api.delete(`/mabar/padel/sessions/${sessionSlug}`);
  },

  async resetEventGames(eventSlug: string): Promise<void> {
    await api.post(`/mabar/events/${eventSlug}/padel/sessions/reset`);
  },

  async updateRoundLineup(
    sessionSlug: string,
    roundNumber: number,
    payload: { sideA: number[]; sideB: number[]; queue: number[]; courtNumber?: number }
  ): Promise<void> {
    await api.patch(`/mabar/padel/sessions/${sessionSlug}/rounds/${roundNumber}/lineup`, payload);
  },

  async updateGameLineup(
    gameSlug: string,
    payload: { sideA: number[]; sideB: number[] }
  ): Promise<void> {
    await api.patch(`/mabar/padel/games/${gameSlug}/lineup`, payload);
  },

  async generateNextRound(sessionSlug: string): Promise<PadelSessionDetail> {
    const res = await api.post(`/mabar/padel/sessions/${sessionSlug}/rounds/next`);
    return res.data.data;
  },
};

export default padelService;
